import { useMemo, useState } from "react";
import { Search, ChevronDown, ChevronUp, MessageCircle, Sparkles } from "lucide-react";
import useSeo, { PROD_DOMAIN } from "../lib/seo";
import {
  TrustPage, TrustHero, TrustSection, ClosingCta, WhatsAppInline,
} from "../components/TrustLayout";

/**
 * /faq — searchable FAQ hub with FAQPage Schema.org markup.
 *
 * Categories ↔ entries. The search is client-side (no backend) for
 * instant filtering and SEO simplicity.
 */
const FAQ_DATA = [
  {
    category: "Getting started",
    icon: "🌿",
    items: [
      {
        q: "What exactly is Feel Greats?",
        a: "Feel Greats is a guided wellness platform — not a supplement store. We help you understand your body with a free assessment, then suggest at most one or two products with a real wellness mentor on call. We focus on the right next step for you, even if that step isn't buying anything.",
      },
      {
        q: "How much does the assessment cost?",
        a: "Nothing. The assessment is free, you can complete it without giving a card, and the resulting wellness report is yours to keep.",
      },
      {
        q: "How long does the assessment take?",
        a: "About two minutes — ten short questions. You can pause and come back any time.",
      },
      {
        q: "Do I have to buy anything to use Feel Greats?",
        a: "No. Many of our members message a wellness mentor for guidance and never purchase. That's by design — we'd rather have a small loyal community than a big disappointed one.",
      },
    ],
  },
  {
    category: "Products & ingredients",
    icon: "🧪",
    items: [
      {
        q: "Where are the products made?",
        a: "All products are formulated by Unicity, a 35-year-old health-science company. We curate from their range, link to the original ingredient PDFs on every product page, and only recommend products with transparent labelling and GMP-certified manufacturing.",
      },
      {
        q: "Can I see the full ingredient list before I buy?",
        a: "Yes. Every product page links to the official ingredient sheet and the full supplement-facts PDF. We never hide doses behind 'proprietary blends'.",
      },
      {
        q: "Are the products vegan/gluten-free/halal?",
        a: "Varies per product — check each product page's PDF for the specifics. If you have multiple dietary restrictions, message a mentor and we'll walk you through what fits.",
      },
      {
        q: "I'm pregnant / nursing / on medication — can I take these?",
        a: "Please talk to your GP first. Our products are wellness-level and most are well tolerated, but we never recommend during pregnancy, breastfeeding, or alongside prescription medication without a clinician's check. Our mentors will pause any recommendation and route you to clinical care.",
      },
    ],
  },
  {
    category: "Shipping & delivery",
    icon: "📦",
    items: [
      {
        q: "Who handles shipping?",
        a: "Unicity ships every order directly — they own the fulfilment, packaging and customer-service for the physical product. We're the guidance layer that helps you choose what's right.",
      },
      {
        q: "How long does delivery take in the UK?",
        a: "Typically 2-4 business days within the UK. International delivery times vary by country — Unicity's checkout will give you an accurate estimate.",
      },
      {
        q: "Do you ship internationally?",
        a: "Unicity ships to most major markets. We currently support UK and USA storefront variants with localized pricing; international visitors can still purchase via the UK store if Unicity supports their region.",
      },
      {
        q: "What if my parcel is delayed or damaged?",
        a: "Drop us a WhatsApp message or email — we'll liaise with Unicity directly so you don't have to. Most issues resolve within 24-48 hours.",
      },
    ],
  },
  {
    category: "Subscriptions & billing",
    icon: "🔄",
    items: [
      {
        q: "How do subscriptions work?",
        a: "Most products offer a 'subscribe & save' option at around 10% off the one-time price. Billing is monthly and managed by Unicity — you can pause, skip a month, or cancel at any time from your Unicity account.",
      },
      {
        q: "Can I cancel anytime?",
        a: "Yes. There are no minimum terms or hidden lock-ins. One tap to cancel.",
      },
      {
        q: "How do I update my payment method or address?",
        a: "Both are managed in your Unicity account (link on every order confirmation email). If you get stuck, message our mentor and we'll guide you through it.",
      },
      {
        q: "What currencies do you support?",
        a: "GBP (UK) and USD (USA). The storefront automatically shows the right currency for your country — use the country pill in the header or footer to switch.",
      },
    ],
  },
  {
    category: "Refunds & guarantees",
    icon: "🛡️",
    items: [
      {
        q: "What's your refund policy?",
        a: "Unicity offers a transparent refund window on every product — see the full policy at /refund or on any product page. If you've started a product and it isn't working for you, message us first and we'll usually find a path that works for both sides.",
      },
      {
        q: "Do you guarantee results?",
        a: "We're honest about this: no responsible wellness brand should guarantee specific results. What we can guarantee is calm guidance, real human support, and an honest 'this isn't right for you' if your profile doesn't fit.",
      },
      {
        q: "What if the assessment recommends something I can't afford?",
        a: "Tell us. We'll suggest a lower-cost path — sometimes that's a smaller product, sometimes it's a habit-first approach. Wellness should never put you in financial discomfort.",
      },
    ],
  },
  {
    category: "Privacy & safety",
    icon: "🔐",
    items: [
      {
        q: "Is my assessment data private?",
        a: "Yes. Your assessment is stored against your account only, encrypted at rest, and never shared with third parties. You can request full deletion at /data-deletion any time.",
      },
      {
        q: "Do you sell my email or data?",
        a: "Never. Your email is used for order updates, mentor check-ins (which you can mute), and the wellness library newsletter (which you can unsubscribe from with one click).",
      },
      {
        q: "Is Feel Greats a medical service?",
        a: "No. Feel Greats is a wellness platform. We never diagnose, treat, or prescribe — and we route safety-relevant questions to your GP. See /science for the full disclaimer.",
      },
    ],
  },
  {
    category: "Support & community",
    icon: "💬",
    items: [
      {
        q: "How fast do you reply to messages?",
        a: "Most WhatsApp messages get a reply within 1-4 working hours during UK business hours (09:00-18:00 GMT). Emails reply within 1 business day. We don't use chatbots — every reply is from a named human.",
      },
      {
        q: "Can I speak with the same mentor each time?",
        a: "Yes — if you'd like continuity, tell us in your first message and we'll keep you with the same mentor across visits, orders and check-ins.",
      },
      {
        q: "Do you have a community or member group?",
        a: "We deliberately don't run a public group — wellness conversations are personal. Mentors message you one-on-one, and the wellness library is open for everyone to read.",
      },
      {
        q: "Where else can I find Feel Greats?",
        a: "We post calmly on Instagram (@feelgreats), and a longer-form wellness library lives at /learn. We don't run paid ads or push social campaigns — most of our growth is word-of-mouth.",
      },
    ],
  },
];

export default function FaqPage() {
  const [query, setQuery] = useState("");
  const [openKey, setOpenKey] = useState(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return FAQ_DATA;
    return FAQ_DATA.map((cat) => ({
      ...cat,
      items: cat.items.filter(
        (i) => i.q.toLowerCase().includes(q) || i.a.toLowerCase().includes(q),
      ),
    })).filter((cat) => cat.items.length > 0);
  }, [query]);

  // Build FAQPage JSON-LD over the full dataset (not filtered) — Google
  // wants every Q&A indexed regardless of UI state.
  const allQAs = FAQ_DATA.flatMap((c) => c.items);
  useSeo({
    title: "FAQ — Feel Greats wellness questions answered",
    description:
      "Quick, honest answers to common questions about assessments, products, shipping, subscriptions, refunds, privacy and wellness mentor support.",
    canonical: `${PROD_DOMAIN}/faq`,
    ogType: "website",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": allQAs.map((it) => ({
          "@type": "Question",
          "name": it.q,
          "acceptedAnswer": { "@type": "Answer", "text": it.a },
        })),
      },
    ],
  });

  return (
    <TrustPage testid="faq-page">
      <TrustHero
        eyebrow="Frequently asked"
        headline="Calm answers to common questions."
        highlightWord="Calm"
        body="If you can't find what you're looking for, message a wellness mentor on WhatsApp — we'd rather have a personal chat than make you scroll forever."
        primaryCta={{ href: "https://wa.me/447424225333?text=Hi%20Feel%20Greats%2C%20I%20have%20a%20question", label: "Ask a mentor on WhatsApp", external: true, testid: "faq-hero-whatsapp", icon: MessageCircle }}
        secondaryCta={{ to: "/contact", label: "Or email us", testid: "faq-hero-email" }}
        testid="faq-hero"
      />

      {/* Search bar */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 -mt-4 sm:-mt-8">
        <div className="rounded-full border border-emerald-200 bg-white shadow-sm flex items-center gap-3 px-5 py-3" data-testid="faq-search-wrap">
          <Search className="w-4 h-4 text-emerald-700/60" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search for shipping, ingredients, refunds…"
            className="flex-1 outline-none text-sm text-emerald-950 placeholder:text-emerald-900/40 bg-transparent"
            data-testid="faq-search-input"
            aria-label="Search FAQs"
          />
          {query && (
            <button type="button" onClick={() => setQuery("")} className="text-[11px] text-emerald-700 hover:text-emerald-800" data-testid="faq-search-clear">Clear</button>
          )}
        </div>
        <p className="text-[11px] text-emerald-900/55 text-center mt-2.5">
          {query ? `Showing matches for "${query}"` : `${allQAs.length} answers across ${FAQ_DATA.length} categories`}
        </p>
      </section>

      {/* Categories + items */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14 space-y-10" data-testid="faq-list">
        {filtered.length === 0 ? (
          <EmptyState />
        ) : (
          filtered.map((cat) => (
            <article key={cat.category} data-testid={`faq-cat-${cat.category.toLowerCase().replace(/\s+/g, "-")}`}>
              <header className="flex items-center gap-2 mb-4">
                <span className="text-xl" aria-hidden>{cat.icon}</span>
                <h2 className="font-display text-2xl text-emerald-950 font-light">{cat.category}</h2>
              </header>
              <ul className="divide-y divide-emerald-100 rounded-2xl border border-emerald-100 bg-white overflow-hidden">
                {cat.items.map((it, idx) => {
                  const key = `${cat.category}-${idx}`;
                  const open = openKey === key;
                  return (
                    <li key={key}>
                      <button
                        type="button"
                        onClick={() => setOpenKey(open ? null : key)}
                        className="w-full text-left flex items-start gap-3 px-5 py-4 hover:bg-emerald-50/40 transition"
                        aria-expanded={open}
                        data-testid={`faq-q-${cat.category.toLowerCase().replace(/\s+/g, "-")}-${idx}`}
                      >
                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-50 text-emerald-700 shrink-0 mt-0.5">
                          {open ? <ChevronUp className="w-3.5 h-3.5" strokeWidth={2} /> : <ChevronDown className="w-3.5 h-3.5" strokeWidth={2} />}
                        </span>
                        <span className="text-sm sm:text-base font-semibold text-emerald-950 flex-1 leading-snug">{it.q}</span>
                      </button>
                      {open && (
                        <div className="px-5 pb-5 pl-14 text-[14.5px] text-emerald-900/85 leading-relaxed" data-testid={`faq-a-${cat.category.toLowerCase().replace(/\s+/g, "-")}-${idx}`}>
                          {it.a}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </article>
          ))
        )}
      </section>

      <TrustSection
        eyebrow="Still curious?"
        title="There's almost always a quicker way: ask a mentor."
        body="A 30-second message gets a tailored reply within hours. Whether it's about your assessment, a specific ingredient, or whether you should even start with us — we'll be honest."
        tone="amber"
        testid="faq-mentor-cta-section"
      >
        <WhatsAppInline testid="faq-mentor-cta-whatsapp" text="Hi Feel Greats! I have a question about…" />
      </TrustSection>

      <ClosingCta
        eyebrow="Or just begin"
        title="Two minutes of clarity."
        body="The free assessment is usually the fastest way to get specific answers about your body."
      />
    </TrustPage>
  );
}

function EmptyState() {
  return (
    <div className="rounded-3xl border border-emerald-100 bg-emerald-50/30 p-10 text-center" data-testid="faq-empty">
      <Sparkles className="w-9 h-9 mx-auto text-emerald-700/40" strokeWidth={1.3} />
      <p className="font-display text-xl text-emerald-950 font-light mt-3">No exact matches.</p>
      <p className="text-sm text-emerald-900/70 mt-2 max-w-md mx-auto">
        Try a shorter keyword, or just ask a mentor — we'll likely have an answer ready.
      </p>
    </div>
  );
}
